// Статистика появления врагов и лута по комнатам. Считается из строк
// подробностей (раздел 1b контракта): строка про seed, внутри — комнаты
// с видом, размером, расстоянием от входа, врагами и записями лута.
//
// Склейка идёт ПО SEED, а не по номеру строки. Файл подробностей может
// быть короче файла прогонов (образец на 50 seed против 200 прогонов),
// и склейка по индексу тихо сопоставила бы чужие уровни.

import type { DetailRow } from './types';

export interface GroupStat {
  key: string;
  rooms: number;
  enemies: number;
  lootPlacements: number;
  lootAmount: number;
  /** Врагов на комнату этого вида. */
  enemiesPerRoom: number;
  lootPerRoom: number;
  /** Доля комнат этого вида, где есть хотя бы один враг. */
  roomsWithEnemyShare: number;
  cells: number;
}

export interface DistanceBand {
  distance: number;
  rooms: number;
  enemies: number;
  lootPlacements: number;
  lootAmount: number;
  enemiesPerRoom: number;
  lootPerRoom: number;
}

export interface SpawnStatistics {
  /** Готовая фраза сервера про отсутствие дропа. Показывается дословно. */
  factsNote: string | null;
  seeds: number;
  rooms: number;
  byKind: GroupStat[];
  bySize: GroupStat[];
  byDistance: DistanceBand[];
  lootByKind: Array<{ kind: string; placements: number; amount: number; share: number }>;
  /** Комнаты с distanceFromEntrance = −1: до них не дойти. Инвариант связности. */
  unreachableRooms: number;
  unreachableSeeds: number[];
  /** Врагов в комнате входа и выхода — отступы должны держать их пустыми. */
  enemiesInEntrance: number;
  enemiesInExit: number;
  maxDistance: number;
}

function emptyGroup(key: string): GroupStat {
  return {
    key,
    rooms: 0,
    enemies: 0,
    lootPlacements: 0,
    lootAmount: 0,
    enemiesPerRoom: 0,
    lootPerRoom: 0,
    roomsWithEnemyShare: 0,
    cells: 0,
  };
}

export function computeSpawns(details: readonly DetailRow[]): SpawnStatistics {
  const byKind = new Map<string, GroupStat>();
  const bySize = new Map<string, GroupStat>();
  const withEnemy = new Map<string, number>();
  const withEnemySize = new Map<string, number>();
  const byDistance = new Map<number, DistanceBand>();
  const lootByKind = new Map<string, { placements: number; amount: number }>();
  const unreachableSeeds = new Set<number>();

  let rooms = 0;
  let unreachableRooms = 0;
  let enemiesInEntrance = 0;
  let enemiesInExit = 0;
  let maxDistance = 0;
  let totalLootAmount = 0;

  for (const row of details) {
    for (const room of row.rooms ?? []) {
      rooms++;
      const lootAmount = room.loot.reduce((sum, l) => sum + l.amount, 0);
      totalLootAmount += lootAmount;

      const kind = byKind.get(room.kind) ?? emptyGroup(room.kind);
      kind.rooms++;
      kind.enemies += room.enemies;
      kind.lootPlacements += room.loot.length;
      kind.lootAmount += lootAmount;
      kind.cells += room.cells;
      byKind.set(room.kind, kind);
      if (room.enemies > 0) withEnemy.set(room.kind, (withEnemy.get(room.kind) ?? 0) + 1);

      const size = bySize.get(room.sizeClass) ?? emptyGroup(room.sizeClass);
      size.rooms++;
      size.enemies += room.enemies;
      size.lootPlacements += room.loot.length;
      size.lootAmount += lootAmount;
      size.cells += room.cells;
      bySize.set(room.sizeClass, size);
      if (room.enemies > 0) withEnemySize.set(room.sizeClass, (withEnemySize.get(room.sizeClass) ?? 0) + 1);

      if (room.distanceFromEntrance < 0) {
        unreachableRooms++;
        unreachableSeeds.add(row.seed);
      } else {
        maxDistance = Math.max(maxDistance, room.distanceFromEntrance);
        const band =
          byDistance.get(room.distanceFromEntrance) ??
          {
            distance: room.distanceFromEntrance,
            rooms: 0,
            enemies: 0,
            lootPlacements: 0,
            lootAmount: 0,
            enemiesPerRoom: 0,
            lootPerRoom: 0,
          };
        band.rooms++;
        band.enemies += room.enemies;
        band.lootPlacements += room.loot.length;
        band.lootAmount += lootAmount;
        byDistance.set(room.distanceFromEntrance, band);
      }

      if (room.isEntrance) enemiesInEntrance += room.enemies;
      if (room.isExit) enemiesInExit += room.enemies;

      for (const drop of room.loot) {
        const entry = lootByKind.get(drop.kind) ?? { placements: 0, amount: 0 };
        entry.placements++;
        entry.amount += drop.amount;
        lootByKind.set(drop.kind, entry);
      }
    }
  }

  const finish = (map: Map<string, GroupStat>, hits: Map<string, number>) =>
    [...map.values()]
      .map((group) => ({
        ...group,
        enemiesPerRoom: group.rooms > 0 ? group.enemies / group.rooms : 0,
        lootPerRoom: group.rooms > 0 ? group.lootPlacements / group.rooms : 0,
        roomsWithEnemyShare: group.rooms > 0 ? (hits.get(group.key) ?? 0) / group.rooms : 0,
      }))
      .sort((a, b) => b.rooms - a.rooms);

  return {
    factsNote:
      'Лут не падает с врагов: механики дропа в игре нет. Всё, что ниже, — размещение лута ' +
      'по комнатам, которое делает LevelPopulator, и оно не связано с тем, где стоят враги.',
    seeds: details.length,
    rooms,
    byKind: finish(byKind, withEnemy),
    bySize: finish(bySize, withEnemySize),
    byDistance: [...byDistance.values()]
      .map((band) => ({
        ...band,
        enemiesPerRoom: band.rooms > 0 ? band.enemies / band.rooms : 0,
        lootPerRoom: band.rooms > 0 ? band.lootPlacements / band.rooms : 0,
      }))
      .sort((a, b) => a.distance - b.distance),
    lootByKind: [...lootByKind.entries()]
      .map(([kind, value]) => ({
        kind,
        placements: value.placements,
        amount: value.amount,
        share: totalLootAmount > 0 ? value.amount / totalLootAmount : 0,
      }))
      .sort((a, b) => b.amount - a.amount),
    unreachableRooms,
    unreachableSeeds: [...unreachableSeeds].sort((a, b) => a - b),
    enemiesInEntrance,
    enemiesInExit,
    maxDistance,
  };
}
